import { useState, useEffect } from 'react';
import { X, Download, ZoomIn, ZoomOut, RotateCw, Maximize2, AlertCircle, FileText, Image as ImageIcon, Loader2, ArrowLeft } from 'lucide-react';

interface DocumentViewerProps {
  documentUrl: string;
  documentName: string;
  mimeType: string;
  onClose: () => void;
  onError?: (error: string) => void;
}

export function DocumentViewer({
  documentUrl,
  documentName,
  mimeType,
  onClose,
  onError,
}: DocumentViewerProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const isImage = mimeType.startsWith('image/');
  const isPdf = mimeType === 'application/pdf';

  useEffect(() => {
    // Reset state when document changes
    setLoading(true);
    setError(null);
    setZoom(100);
    setRotation(0);
  }, [documentUrl]);

  const handleImageLoad = () => {
    setLoading(false);
  };

  const handleImageError = () => {
    setLoading(false);
    const errorMsg = 'Não foi possível carregar a imagem. O arquivo pode estar corrompido ou inacessível.';
    setError(errorMsg);
    onError?.(errorMsg);
  };

  const handlePdfLoad = () => {
    setLoading(false);
  };

  const handlePdfError = () => {
    setLoading(false);
    const errorMsg = 'Não foi possível carregar o PDF. O arquivo pode estar corrompido ou inacessível.';
    setError(errorMsg);
    onError?.(errorMsg);
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 25, 300));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 25, 25));
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = documentUrl;
    link.download = documentName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm ${
        isFullscreen ? '' : 'p-4'
      }`}
      onClick={handleBackdropClick}
    >
      <div
        className={`bg-surface-900 rounded-xl shadow-2xl flex flex-col ${
          isFullscreen ? 'w-full h-full rounded-none' : 'max-w-5xl max-h-[90vh] w-full'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-700">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-2 hover:bg-surface-700 rounded-lg transition-colors text-surface-400 hover:text-white"
              title="Voltar"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              {isImage ? (
                <ImageIcon className="w-5 h-5 text-primary-400" />
              ) : (
                <FileText className="w-5 h-5 text-primary-400" />
              )}
              <span className="text-white font-medium truncate max-w-md">
                {documentName}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Zoom controls (only for images) */}
            {isImage && !error && (
              <>
                <button
                  onClick={handleZoomOut}
                  className="p-2 hover:bg-surface-700 rounded-lg transition-colors text-surface-400 hover:text-white"
                  title="Diminuir zoom"
                  disabled={zoom <= 25}
                >
                  <ZoomOut className="w-5 h-5" />
                </button>
                <span className="text-surface-400 text-sm min-w-[4rem] text-center">
                  {zoom}%
                </span>
                <button
                  onClick={handleZoomIn}
                  className="p-2 hover:bg-surface-700 rounded-lg transition-colors text-surface-400 hover:text-white"
                  title="Aumentar zoom"
                  disabled={zoom >= 300}
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
                <button
                  onClick={handleRotate}
                  className="p-2 hover:bg-surface-700 rounded-lg transition-colors text-surface-400 hover:text-white"
                  title="Rotacionar"
                >
                  <RotateCw className="w-5 h-5" />
                </button>
              </>
            )}

            <button
              onClick={toggleFullscreen}
              className="p-2 hover:bg-surface-700 rounded-lg transition-colors text-surface-400 hover:text-white"
              title={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
            >
              <Maximize2 className="w-5 h-5" />
            </button>

            <button
              onClick={handleDownload}
              className="p-2 hover:bg-surface-700 rounded-lg transition-colors text-surface-400 hover:text-white"
              title="Baixar documento"
            >
              <Download className="w-5 h-5" />
            </button>

            <button
              onClick={onClose}
              className="p-2 hover:bg-surface-700 rounded-lg transition-colors text-surface-400 hover:text-white"
              title="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-surface-950">
          {loading && (
            <div className="flex flex-col items-center gap-3 text-surface-400">
              <Loader2 className="w-8 h-8 animate-spin" />
              <span>Carregando documento...</span>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center gap-4 text-center max-w-md">
              <div className="p-4 bg-red-500/10 rounded-full">
                <AlertCircle className="w-12 h-12 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-white mb-2">
                  Erro ao carregar documento
                </h3>
                <p className="text-surface-400 mb-4">{error}</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-surface-700 hover:bg-surface-600 text-white rounded-lg transition-colors"
                >
                  Voltar
                </button>
                <button
                  onClick={handleDownload}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Baixar arquivo
                </button>
              </div>
            </div>
          )}

          {!error && isImage && (
            <div
              className="transition-transform duration-200"
              style={{
                transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
              }}
            >
              <img
                src={documentUrl}
                alt={documentName}
                onLoad={handleImageLoad}
                onError={handleImageError}
                className={`max-w-full max-h-full object-contain ${
                  loading ? 'invisible' : 'visible'
                }`}
                style={{ maxHeight: isFullscreen ? '100vh' : '70vh' }}
              />
            </div>
          )}

          {!error && isPdf && (
            <iframe
              src={`${documentUrl}#toolbar=1&navpanes=0`}
              title={documentName}
              onLoad={handlePdfLoad}
              onError={handlePdfError}
              className={`w-full h-full min-h-[60vh] rounded-lg border border-surface-700 ${
                loading ? 'invisible' : 'visible'
              }`}
              style={{ height: isFullscreen ? '100%' : '70vh' }}
            />
          )}

          {!error && !isImage && !isPdf && (
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="p-4 bg-surface-800 rounded-full">
                <FileText className="w-12 h-12 text-surface-400" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-white mb-2">
                  Formato não suportado para visualização
                </h3>
                <p className="text-surface-400 mb-4">
                  Este tipo de arquivo ({mimeType}) não pode ser visualizado diretamente.
                </p>
              </div>
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Baixar arquivo
              </button>
            </div>
          )}
        </div>

        {/* Footer with document info */}
        {!error && !loading && (
          <div className="px-4 py-2 border-t border-surface-700 text-sm text-surface-400">
            <span>Tipo: {mimeType}</span>
            {isImage && (
              <span className="ml-4">
                Zoom: {zoom}% | Rotação: {rotation}°
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default DocumentViewer;
